import { useWorkspaceStore } from "@/hooks/useWorkspace";
import React, { useState, useEffect } from "react";
import { getCookie } from "@/lib/cookies";
import { API_URL } from "@/env";
import { showCustomToast } from "../CustomToast";

const General = () => {
  const { currentWorkspace, setWorkspaces, workspaces, setCurrentWorkspace } =
    useWorkspaceStore();

  const [updateError, setUpdateError] = useState("");

  const [workspaceName, setWorkspaceName] = useState(
    currentWorkspace?.name || ""
  );
  const [isUpdating, setIsUpdating] = useState(false);

  const token = getCookie("token");

  const handleUpdateWorkspaceName = async () => {
    if (!workspaceName.trim()) {
      setUpdateError("Workspace name cannot be empty");
      return;
    }

    if (workspaceName === currentWorkspace?.name) {
      return; // No change, don't make API call
    }

    setIsUpdating(true);
    setUpdateError("");

    try {
      const response = await fetch(
        `${API_URL}/workspaces/${currentWorkspace?._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: workspaceName.trim(),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update workspace name");
      }

      const data = await response.json();

      if (data._id) {
        setCurrentWorkspace(data);
        setWorkspaces(
          workspaces.map((workspace) => {
            if (workspace._id === data._id) {
              return { ...data };
            } else {
              return workspace;
            }
          })
        );
      }
      // console.log({ data });
      showCustomToast("Workspace name updated", "check");

      // Optionally refresh the workspace data
      // await fetchWorkspaces(); // or however you update your workspace store
    } catch (error: any) {
      setUpdateError(error.message || "Failed to update workspace name");
      // Revert to original name on error
      setWorkspaceName(currentWorkspace?.name || "");
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    if (currentWorkspace?.name) {
      setWorkspaceName(currentWorkspace.name);
    }
  }, [currentWorkspace?.name]);

  return (
    <div className="flex gap-6 items-center">
      <div className="relative text-[32px] leading-[48px] font-semibold">
        <div
          style={{ color: currentWorkspace?.color || "#FF0088" }}
          className="absolute uppercase top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-white z-[10]"
        >
          {currentWorkspace?.name[0]}
        </div>
        <div
          style={{ background: currentWorkspace?.color || "#FF0088" }}
          className="opacity-[0.16] uppercase w-[72px] h-[72px] flex items-center text-[12px] leading-[16px] font-semibold text-gray-900 justify-center rounded-[20px]"
        />
      </div>
      <div className="w-full">
        <p className="text-[14px] leading-[20px] text-white/[0.32] font-medium">
          Workspace name
        </p>
        <input
          type="text"
          placeholder="Enter workspace name"
          className={`w-full rounded-[12px] h-[36px] flex-1 mt-2 text-[14px] leading-[20px] px-4 outline-none ring-0 placeholder:text-white/[0.32] text-white bg-white/[0.04] border-[1px] ${
            updateError ? "border-red-700" : "border-white/[0.08]"
          } py-[8px]`}
          value={workspaceName}
          onChange={(e) => {
            setWorkspaceName(e.target.value);
            setUpdateError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && workspaceName.trim()) {
              handleUpdateWorkspaceName();
              e.currentTarget.blur();
            }
          }}
          onBlur={handleUpdateWorkspaceName}
          disabled={isUpdating}
        />
        {updateError && (
          <p className="text-red-700 text-[12px] leading-[16px] mt-2">
            {updateError}
          </p>
        )}
      </div>
    </div>
  );
};

export default General;
